
import React, { memo, useState, useEffect, useRef } from "react";
import { Toast, ToastClose } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Undo } from "lucide-react";
import { ToastTitle, ToastDescription } from "@/components/ui/typography";
import { useUndoToast } from "@/hooks/undoCard/useUndoToast";

interface UndoToastProps {
  title: string;
  description: string;
  duration?: number; // duration in ms, now optional
  onUndo: () => void;
}

// Using memo to prevent unnecessary re-renders
export const UndoToast = memo<UndoToastProps>(({
  title,
  description,
  duration,
  onUndo,
}) => {
  const { handleUndo } = useUndoToast(title, duration, onUndo);
  const [progress, setProgress] = useState<number>(100);
  const animationStarted = useRef(false);
  
  useEffect(() => {
    if (duration && !animationStarted.current) {
      // Start the animation on the next frame to ensure the component is mounted
      const frameId = requestAnimationFrame(() => {
        setProgress(0);
        animationStarted.current = true;
      });
      
      return () => {
        cancelAnimationFrame(frameId);
      };
    }
  }, [duration]); // Only depend on duration, not other props that might change
  
  return (
    <Toast className="w-full">
      <div className="grid gap-1">
        <ToastTitle>{title}</ToastTitle>
        <ToastDescription>{description}</ToastDescription>
      </div>
      
      {duration && (
        <div className="mt-4 mb-2">
          <Progress 
            value={progress} 
            className="h-1 bg-mono-slateGray/20" 
            indicatorClassName="bg-mono-platinum/60" 
            transitionDuration={duration}
          />
        </div>
      )}
      
      <div className="flex justify-end mt-2">
        <Button 
          onClick={handleUndo} 
          variant="outline"
          size="sm"
          title="Undo"
          className="text-mono-platinum hover:text-mono-seasalt hover:bg-mono-outerSpace/30 border-mono-slateGray/30"
        >
          <Undo className="mr-2 h-3.5 w-3.5" />
          <span>Undo</span>
        </Button>
      </div>
      
      <ToastClose />
    </Toast>
  );
});

UndoToast.displayName = "UndoToast";
